/**
 * leadRef — the privacy-preserving handle to a diagnostic lead.
 *
 * The backend keys every lead doc by `sha256(email)` (leads/{hash}, see
 * captureLead.ts / updateLeadPersonalization.ts). To mirror pre-account
 * personalize answers into that doc WITHOUT ever holding the raw email
 * client-side, we compute the same hash at capture time and stash ONLY the
 * hash in sessionStorage. The /onboarding flow reads it back to address the
 * lead by hash — the raw email never touches storage.
 *
 * `computeLeadId` MUST match the backend derivation byte-for-byte:
 * lowercase-hex SHA-256 of the trimmed, lowercased email.
 */
import { logger } from '@/utils/logger';

const LEAD_REF_KEY = 'swtpa_lead_ref';
const LEAD_ID_RE = /^[0-9a-f]{64}$/;

/**
 * Lowercase-hex SHA-256 of the trimmed, lowercased email — the ONE canonical
 * email hash for this app. It is the backend leadId (leads/{hash}), the Meta
 * pixel's advanced-matching `em` value, AND the stem of the Lead CAPI event_id,
 * so all three match byte-for-byte for dedup. Reused by metaPixel.hashEmail —
 * do NOT duplicate this SHA-256 elsewhere.
 */
export async function sha256HexNormalizedEmail(email: string): Promise<string> {
  const normalized = email.trim().toLowerCase();
  const bytes = new TextEncoder().encode(normalized);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Lowercase-hex SHA-256 of the normalized email — matches the backend leadId. */
export async function computeLeadId(email: string): Promise<string> {
  return sha256HexNormalizedEmail(email);
}

/**
 * Persist ONLY the lead hash (never the email) for this session. sessionStorage
 * (not localStorage) so it clears when the tab closes — the hash is a same-visit
 * onboarding handle, not durable state.
 */
export function saveLeadRef(leadId: string): void {
  if (!LEAD_ID_RE.test(leadId)) return;
  try {
    sessionStorage.setItem(LEAD_REF_KEY, leadId);
  } catch (err) {
    // Private mode / storage disabled — the mirror is best-effort, so swallow.
    logger.warn('leadRef', 'saveLeadRef failed (non-fatal)', { err });
  }
}

/** Read back the stashed lead hash, or null if absent / unreadable / malformed. */
export function loadLeadRef(): string | null {
  try {
    const v = sessionStorage.getItem(LEAD_REF_KEY);
    return v && LEAD_ID_RE.test(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * The lead hash from a Pass Plan token's `id` claim, read WITHOUT verifying the
 * signature (redeemPassPlanToken verifies server side). Null for a malformed token.
 */
export function leadIdFromPassPlanToken(token: string | null): string | null {
  try {
    const payload = token?.split('.')[0];
    if (!payload) return null;
    const id: unknown = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))).id;
    return typeof id === 'string' && LEAD_ID_RE.test(id) ? id : null;
  } catch {
    return null;
  }
}
